import DOMPurify from 'dompurify';
const fillerWords = '\\+|\\&|and|of|the|for|with|on|in|at'.split('|');

// event stopper
const stop = (e) => {
	if (e?.preventDefault) {
		e.preventDefault();
		e.stopPropagation();
	}
	return true;
}

const utils = {
	cleanTerm: (term) => {
		return utils.cleanString(String(term).replace(/["|;&$%#<>()+\?\~\*]/g, ''));
	},

	stripFillerWords: (term) => {
		term = utils.cleanString(term);
		fillerWords.forEach((fillerWord) => {
			term = term.replace(new RegExp('\\b' + fillerWord + '\\b', 'gi'), '');
		});
		return term.replace(/\s\s+/, ' ').trim();
	},

	isEmpty: (val) => {
		return (
			'' === val || undefined === val || null === val || (utils.isObject(val) && !utils.objectKeys(val).length)
		);
	},

	isObject: (item) => {
		return item && 'object' === typeof item && !Array.isArray(item);
	},

	mergeDeep: (target, source) => {
		if (!source) {
			return target;
		}
		if (utils.isObject(target) && utils.isObject(source)) {
			for (const key in source) {
				if (utils.isObject(source[key])) {
					if (!target[key]) {
						Object.assign(target, { [key]: {} });
					}
					utils.mergeDeep(target[key], source[key]);
				} else {
					Object.assign(target, { [key]: source[key] });
				}
			}
		}
		return target;
	},

	// Utilities
	stripTags: (input, allowed) => {
		allowed = (
			String(allowed || '')
				.toLowerCase()
				.match(/<[a-z][a-z0-9]*>/g) || []
		).join(''); // making sure the allowed arg is a string containing only tags in lowercase (<a><b><c>)
		var tags = /<\/?([a-z][a-z0-9]*)\b[^>]*>/gi,
			commentsAndPhpTags = /<!--[\s\S]*?-->|<\?(?:php)?[\s\S]*?\?>/gi,
			brokenTags = /(<\w+(?:\s+\w+=\"[^"]+\")*)(?=[^>]+(?:<|$))/g;
		return input
			.replace(commentsAndPhpTags, '')
			.replace(brokenTags, '')
			.replace(tags, function ($0, $1) {
				return -1 < allowed.indexOf('<' + $1.toLowerCase() + '>') ? $0 : '';
			});
	},

	titleCase: (string) => {
		return String(string)
			.toLowerCase()
			.split(' ')
			.map(function (word) {
				return utils.ucFirst(word);
			})
			.join(' ');
	},

	ucFirst: (string) => {
		string = String(string);
		return string.charAt(0).toUpperCase() + string.slice(1);
	},

	lcFirst: (string) => {
		string = String(string);
		return string.charAt(0).toLowerCase() + string.slice(1);
	},

	trailingSlash: (string) => {
		return utils.cleanString(string).replace(/([^\/])$/, '$1/');
	},

	unTrailingSlash: (string) => {
		return utils.cleanString(string).replace(/\/$/, '');
	},

	escapeString: (string, forAttribute) => {
		var ESC_MAP = {
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			"'": '&#39;',
		};
		return String(string).replace(forAttribute ? /[&<>'"]/g : /[&<>]/g, function (c) {
			return ESC_MAP[c];
		});
	},

	boldTerm: (line, words, maxlength) => {
		return utils.boldTerms(line, words, maxlength);
	},

	boldTerms: (line, words, maxlength) => {
		var result = String(line || '');
		words = utils.toArray(words);
		if ('number' === typeof maxlength && result.length > maxlength) {
			result = utils.truncateString(result, maxlength, '');
		}
		words.forEach((word) => {
			if (word) {
				let regex = new RegExp('(' + utils.cleanTerm(word) + ')', 'gi');
				result = result.replace(regex, '<b>$1</b>');
			}
		});
		return result;
	},

	unique: (array) => {
		array = array.filter(function (el) {
			return Boolean(el);
		});
		return array.filter(function (el, index, arr) {
			return index === arr.indexOf(el);
		});
	},

	arrayDiff: (a, b) => {
		if (!Array.isArray(a)) {
			return [];
		}
		if (!Array.isArray(b)) {
			b = [b];
		}
		var c = a.slice();
		b.forEach((term, i) => {
			var index = c.indexOf(term);
			if (-1 < index) {
				c.splice(index, 1);
			}
		});
		return c;
	},

	pad: (num, size, padding) => {
		padding = padding || '0';
		var s = String(num);
		size = size || 2;
		while (s.length < size) {
			s = padding + s;
		}
		return s;
	},

	/**
	 * Given a term, return an array of cleaned terms
	 * @param term
	 * @returns array
	 */
	getWords: (term) => {
		if (undefined === term || null === term) {
			return [];
		}
		var words = utils
			.cleanTerm(term)
			.trim()
			.split(/[\s,]+/);
		var result = [];
		words.forEach(function (word) {
			if ('&' === word) {
				word = 'and';
			}
			if (word) {
				result.push(word);
			}
		});
		return utils.unique(result);
	},

	getParam: (name, defaultValue) => {
		defaultValue = 'undefined' === typeof defaultValue ? null : defaultValue;
		name = name.replace(/[\[\]]/g, '\\$&');
		var url = String((window && window.location && window.location.href) || '');
		if (!url) {
			return defaultValue;
		}
		var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
			results = regex.exec(url);
		if (!results) {
			return defaultValue;
		}

		if (!results[2]) {
			return '';
		}
		return decodeURIComponent(results[2].replace(/\+/g, ' ')).replace(/[<>]/g, '');
	},

	getParamSwitch: (name, defaultValue) => {
		var param = utils.getParam(name);
		var value = false;
		if ('string' !== typeof param) {
			return Boolean(defaultValue);
		}
		if ('' === param) {
			return true;
		}
		param = String(param).trim().toLowerCase();
		if (param.match(/^\d+$/)) {
			value = Boolean(parseInt(param, 10));
		} else if (param.match(/^(n|no|f|false|off)$/)) {
			value = false;
		} else {
			value = true;
		}
		return value;
	},

	maybeJoin: (value, glue) => {
		if (Array.isArray(value)) {
			return value.join(glue).trim();
		}
		return String(value).trim();
	},

	arrayFirst: (value) => {
		var response = '';
		if (value === undefined || null === value) {
			return response;
		}
		if (!Array.isArray(value)) {
			value = [String(value)];
		}
		while (value.length) {
			response = String(value.shift()).trim();
			if (response) {
				return response;
			}
		}
		return response;
	},

	fromPiped: (string) => {
		string = utils.cleanString(string);
		return !string ? [] : string.split(/\s*\|\s*/);
	},

	toPiped: (array) => {
		array = utils.toArray(array);
		return array.join('|');
	},

	arrayToQuery: (array) => {
		let results = [];
		array = utils.toArray(array);
		if (!array.length) {
			return '';
		}
		array.forEach((item) => {
			results.push('"' + utils.cleanTerm(item) + '"');
		});
		return results.join(',');
	},

	objectKeys: (object) => {
		var keys = [];
		if (utils.isObject(object)) {
			for (let key in object) {
				if (object.hasOwnProperty(key) && object[key]) {
					keys.push(key);
				}
			}
		}
		return keys;
	},

	objectValues: (object) => {
		var values = [];
		if (utils.isObject(object)) {
			for (let key in object) {
				if (object.hasOwnProperty(key) && object[key]) {
					values.push(object[key]);
				}
			}
		}
		return values;
	},

	// Specifically go through all object values and if no valid strings, true
	isFormEmpty: (form) => {
		let formCopy = Object.assign({}, form);
		// When we're evaluating the form, don't treat dpage (page number) as a populated field
		let skipped = ['dpage', 'sitelimit', 'siteLimit', 'dateRange', 'date1', 'date2', 'strict', 'checkedFilters'];
		skipped.forEach((skip) => {
			if (formCopy[skip]) {
				delete formCopy[skip];
			}
		});
		let values = utils.objectValues(formCopy);
		return !values.join('').trim();
	},

	formFiltersEmpty: (form) => {
		let empty = (1 === parseInt(form.dpage||1) && !form.all && !form.none && !form.exact && !form.any);
		if (form.checkedFilters) {
			Object.values(form.checkedFilters).forEach(filter => {
				empty = empty && !filter.length;
			})
		}
		return empty;
	},

	cleanString: (string) => {
		return String(string)
			.replace(/[\n\r\s]+/g, ' ')
			.trim();
	},

	cleanOutputString: (string) => {
		let outString = '';
		string = String(string);
		string.split('').forEach((char) => {
			let code = char.charCodeAt(0);
			if (9000 > code) {
				outString += char;
			}
		});
		return utils.cleanString(outString);
	},

	// strip smart and accented characters
	cleanChars: (string) => {
		string = utils.cleanString(string);
		return string
			.replace(/&rsquo;\b/g, "'")
			.replace(/&lsquo;\b/g, "'")
			.replace(/&apos;\b/g, "'")
			.replace(/&gt;\b/g, '>')
			.replace(/&lt;\b/g, '<')
			.replace(/&ldquo;\b/g, '"')
			.replace(/&rdquo;\b/g, '"')
			.replace(/[\u2018\u2019]/g, "'") // smart single quotes
			.replace(/[\u201C\u201D]/g, '"')
			.replace(new RegExp(/[àáâãäå]/g), 'a')
			.replace(new RegExp(/æ/g), 'ae')
			.replace(new RegExp(/ç/g), 'c')
			.replace(new RegExp(/[èéêë]/g), 'e')
			.replace(new RegExp(/[ìíîï]/g), 'i')
			.replace(new RegExp(/ñ/g), 'n')
			.replace(new RegExp(/[òóôõö]/g), 'o')
			.replace(new RegExp(/œ/g), 'oe')
			.replace(new RegExp(/[ùúûü]/g), 'u')
			.replace(new RegExp(/[ýÿ]/g), 'y');
	},

	truncateString: (string, length = 230, suffix = '&hellip;') => {
		let result = utils.cleanString(string);
		if (length && 'number' === typeof length) {
			let regex = new RegExp('^(.{' + parseInt(length, 10) + '}[\\S]*)');
			let matches = String(result).match(regex);
			if (matches && matches.length) {
				let tempResult = matches.shift();
				if (tempResult.length < result.length) {
					result = tempResult + suffix;
				}
			}
		}
		return result;
	},

	cleanHTML: (html) => {
		return DOMPurify.sanitize(utils.cleanString(html));
	},

	toArray: (value) => {
		if (!Array.isArray(value)) {
			return [value];
		}
		return value;
	},

	toNumber: (value) => {
		if ('number' === typeof value) {
			return value;
		}
		return parseInt(String(value).replace(/[^\d\.]+/g, '') || 0);
	},

	toFloat: (value) => {
		if ('number' === typeof value) {
			return value;
		}
		return parseFloat(String(value).replace(/[^\d\.]+/g, '') || 0);
	},

	// Builds a prop url given parts, no extra or missing slashes
	buildPath: (...args) => {
		var path = '';
		for (let key in args) {
			let part = String(args[key]).trim();
			if (!part) {
				continue;
			}
			if (path.match(/\/$/) && part.match(/^\//)) {
				part = part.replace(/^\//, '');
			}
			if (path && !path.match(/\/$/) && !part.match(/^\//)) {
				part = '/' + part;
			}
			path += part;
		}
		return path;
	},

	buildQueryParams: (params) => {
		let parts = [];

		for (let key in params) {
			if (params.hasOwnProperty(key)) {
				let value = params[key];

				// Skip if the value is an object (and not an array)
				if (typeof value === 'object' && !Array.isArray(value)) {
					continue;
				}

				// If value is an array, encode it as a piped string
				if (Array.isArray(value) && value.length) {
					parts.push(key + '=' + utils.toPiped(value.map((item) => encodeURIComponent(item))));
					continue;
				}

				// Clean and encode the value if it's a valid term
				value = utils.cleanTerm(value);
				if (value) {
					parts.push(key + '=' + encodeURIComponent(value));
				}
			}
		}

		return '?' + parts.join('&');
	},

	intVal: (number) => {
		return parseInt(String(number).replace(/\D+/g, ''));
	},

	// Formats seconds to a minutes / seconds timestamp
	formatMSS: (seconds) => {
		seconds = utils.intVal(seconds);
		var h = Math.floor(seconds / 3600);
		var m = Math.floor((seconds % 3600) / 60);
		var s = Math.floor((seconds % 3600) % 60);
		if (h) {
			return h + ':' + utils.pad(m) + ':' + utils.pad(s);
		}
		return m + ':' + utils.pad(s);
	},

	formatDate: (date) => {
		try {
			let d = new Date(utils.cleanString(date));
			if (isNaN(d.getSeconds())) {
				return '';
			}
			return new Intl.DateTimeFormat('en', { dateStyle: 'medium' }).format(d);
		} catch (error) {
			return '';
		}
	},

	toDate: (date) => {
		let d = new Date(utils.cleanString(date));
		if (isNaN(d.getSeconds())) {
			return null;
		}
		return d;
	},

	mergeResults: (arrayOfResults, skipIdsCheck) => {
		var ids = [];
		var output = [];
		arrayOfResults.forEach((results) => {
			results.forEach((result) => {
				if (-1 === ids.indexOf(result.id) || skipIdsCheck) {
					output.push(result);
					ids.push(result.id);
				}
			});
		});
		return output;
	},

	getResultFileType: (result) => {
		result = result || {};
		let type = result.type || '';
		let url = result.url || '';
		let ext = url.replace(/^.*\.(\w+)$/, '$1');
		let fileType = '';
		switch (type) {
			case 'application/pdf':
				fileType = 'pdf';
				break;
			case 'application/doc':
			case 'application/vnd.openxmlformats-officedocument.wordprocessingml.template':
			case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
				fileType = 'doc';
				break;
			case 'application/vnd.sealed-ppt':
			case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
			case 'application/vnd.ms-powerpoint':
				fileType = 'ppt';
				break;
			case 'application/vnd.ms-excel':
				fileType = 'xls';
				break;
		}
		if (!fileType) {
			switch (ext) {
				case 'pdf':
					fileType = 'pdf';
					break;
				case 'ppt':
				case 'pptx':
					fileType = 'ppt';
					break;
				case 'doc':
				case 'docx':
				case 'rtf':
					fileType = 'doc';
					break;
				case 'xls':
				case 'xlsx':
					fileType = 'xls';
					break;
			}
		}
		return fileType;
	},

	// abreviates month to 3 characters
	formatMonth: (month) => {
		switch(month) {
            case 1:
                month = 'Jan';
                break;
            case 2:
                month = 'Feb';
                break;
            case 3:
                month = 'Mar';
                break;
            case 4:
                month = 'Apr';
                break;
            case 5:
                month = 'May';
                break;
            case 6:
                month = 'Jun';
                break;
            case 7:
                month = 'Jul';
                break;
            case 8:
                month = 'Aug';
                break;
            case 9:
                month = 'Sep';
                break;
            case 10:
                month = 'Oct';
                break;
            case 11:
                month = 'Nov';
                break;
            case 12:
                month = 'Dec';
                break;
            default:
                month = '';
        }
        return month;
	},

	calcCurrentDate: () => {
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth() +1;
        const currentDay = today.getDate();
        const curMonth = utils.formatMonth(currentMonth);

        const date = `${curMonth} ${currentDay}, ${currentYear}`;
        return date;
    },

    calcDateOffset: (range) => {
		// calculating option offset and variables for date1
        const today = new Date();
        const dateOffset = (24 * 60 * 60 * 1000) * parseInt(range);
        const prevDate = new Date( today.setTime(today.getTime() - dateOffset) );
        const date1Year = prevDate.getFullYear();
        const date1Month = prevDate.getMonth() +1;
        const date1Day = prevDate.getDate();
        const date1ForMonth = utils.formatMonth(date1Month);

        const date = `${date1ForMonth} ${date1Day}, ${date1Year}`;
        return date;
    },

	// related content has UTF-8 encoding issues
	cleanRelated: ( related ) => {
		function allReplace( str, obj ) {
			for ( const x in obj ) {
				str = str.replace( new RegExp( x, 'g' ), obj[x] );
			}
			return str;
		}
		return allReplace( related, {
			'â€™': "'",
			'â€“': '-',
			'â€š': '‚',
			'â€ž': '„',
			'â€¦': '…',
			'Ë†': 'ˆ',
			'â€¹': '‹',
			'â€”': '—',
			'â€¢': '•',
			'â€': '”',
			'â€œ': '“',
			'â€˜': '‘',
			'Ëœ': '˜',
			'â„¢': '™',
			'Â¼': '¼',
			'Â½': '½',
			'Â¾': '¾',
			'Â°': '°',
			'Â£': '£',
			'Â¢': '¢',
			'Â¡': '¡',
			'Ã€': 'À',
			'Ã': 'Á',
			'Ã‚': 'Â',
			'Ãƒ': 'Ã',
			'Ã„': 'Ä',
			'Ã†': 'Æ',
			'Ã‡': 'Ç',
			'Ãˆ': 'È',
			'Ã‰': 'É',
			'ÃŠ': 'Ê',
			'Ã‹': 'Ë',
			'ÃŒ': 'Ì',
			'Ã': 'Í',
			'ÃŽ': 'Î',
			'Ã': 'Ï',
			'Ã': 'Ð',
			'Ã‘': 'Ñ',
			'Ã’': 'Ò',
			'Ã“': 'Ó',
			'Ã”': 'Ô',
			'Ã•': 'Õ',
			'Ã–': 'Ö',
			'Ã—': '×',
			'Ã˜': 'Ø',
			'Ã™': 'Ù',
			'Ãš': 'Ú',
			'Ã›': 'Û',
			'Ãœ': 'Ü',
			'Ã': 'Ý',
			'Ãž': 'Þ',
			'ÃŸ': 'ß',
			'Ã': 'à',
			'Ã¡': 'á',
			'Ã¢': 'â',
			'Ã£': 'ã',
			'Ã¤': 'ä',
			'Ã¥': 'å',
			'Ã¦': 'æ',
			'Ã§': 'ç',
			'Ã¨': 'è',
			'Ã©': 'é',
			'Ãª': 'ê',
			'Ã«': 'ë',
			'Ã¬': 'ì',
			'Ã': 'í',
			'Ã®': 'î',
			'Ã¯': 'ï',
			'Ã°': 'ð',
			'Ã±': 'ñ',
			'Ã²': 'ò',
			'Ã³': 'ó',
			'Ã´': 'ô',
			'Ãµ': 'õ',
			'Ã¶': 'ö',
			'Ã·': '÷',
			'Ã¸': 'ø',
			'Ã¹': 'ù',
			'Ãº': 'ú',
			'Ã»': 'û',
			'Ã¼': 'ü',
			'Ã½': 'ý',
			'Ã¾': 'þ',
			'Ã¿': 'ÿ',
			'â‰¥': '≥',
			'í³': 'ó',
			'í©': 'é',
			'í±': 'ñ',
			'í¡': 'á',

		} );
	},

	// joins a list of JSX objects
	jsxJoin: (list, glue) => {
		glue = String(glue || ', ');
		return list.reduce((acc, x) => (null === acc ? [x] : [acc, glue, x]), null);
	},

	// scroll to results top
	toTop: () => {
		document.getElementById('results')?.scrollIntoView()
	},

	stop,

	// Debounce utility
	debounce: (func, delay) => {
		let timer;
		return (...args) => {
			clearTimeout(timer);
			timer = setTimeout(() => {
				func(...args);
			}, delay);
		};
	},
};

export {
	utils as default,
	utils,
	stop
};
